import React from 'react';
import { format } from 'date-fns';
import GroupRow from './GroupRow';
import LiquidFillLoader from '../../../shared/components/ui/LiquidFillLoader';
import { DEFAULT_GROUP_COLUMNS, FIXED_GROUP_COLUMNS, getGroupColumnDefinition } from '../config/groupColumns';
import { TABLE_STYLES } from '../styles/dataTableStyles';

const GroupsTable = ({
  groups,
  loading,
  loadingMore = false,
  error,
  totalGroups,
  hasMore = false,
  onLoadMore,
  hasSearchFilter,
  searchTerm,
  columnConfig,
  onRowClick,
  groupKey,
  /** Merged keys from API + prefs (optional); improves headers for columns not on current page */
  knownCustomPropKeys = []
}) => {
  const config = columnConfig || DEFAULT_GROUP_COLUMNS;
  const customPropKeys = React.useMemo(() => {
    const keys = new Set(knownCustomPropKeys);
    (groups || []).forEach((g) => {
      Object.keys(g.customProperties || {}).forEach((k) => keys.add(k));
    });
    return [...keys];
  }, [groups, knownCustomPropKeys]);

  const fixedGroupKeys = Object.keys(FIXED_GROUP_COLUMNS);
  let visibleOrder = (config.order || []).filter((key) => (config.enabled || []).includes(key));
  fixedGroupKeys.forEach((f) => {
    if (!visibleOrder.includes(f)) visibleOrder.unshift(f);
  });
  const styles = TABLE_STYLES;

  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="flex flex-col items-center justify-center px-6 py-12">
          <LiquidFillLoader size={100} />
          <span className="mt-4 text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            Loading groups...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="px-6 py-8 text-center" style={{ color: '#ef4444', fontFamily: 'Raleway, sans-serif' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (!groups || groups.length === 0) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="px-6 py-8 text-center" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          {hasSearchFilter && searchTerm ? <>No groups found matching your search.</> : <>No groups found.</>}
        </div>
      </div>
    );
  }

  const lastResultDate = groups.length > 0 && groups[0].updatedAt
    ? format(new Date(groups[0].updatedAt), 'MMM d, yyyy, h:mm:ss a')
    : null;

  return (
    <>
      <div className="mb-2 px-1 text-left">
        <div className="text-xs text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
          Showing <span className="font-semibold">{groups.length}</span> most recent results of{' '}
          {Number(totalGroups || 0).toLocaleString()} matches
        </div>
      </div>

      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="overflow-x-auto">
          <table className="w-full" style={{ fontFamily: 'Raleway, sans-serif' }}>
            <thead className="sticky top-0 z-10 bg-gray-100">
              <tr style={{ borderBottom: `2px solid ${styles.borderColor}` }}>
                {visibleOrder.map((key) => {
                  const def = getGroupColumnDefinition(key, customPropKeys);
                  const label = def ? def.label : key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  const isFirst = visibleOrder[0] === key;
                  return (
                    <th
                      key={key}
                      className={`px-6 py-3 text-xs font-semibold uppercase tracking-wider bg-gray-100 ${isFirst ? 'text-left' : 'text-center'}`}
                      style={{ color: styles.headerText }}
                    >
                      {label}
                    </th>
                  );
                })}
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider bg-gray-100" style={{ color: styles.headerText }} />
              </tr>
            </thead>
            <tbody>
              {groups.map((group, index) => (
                <GroupRow
                  key={`${group.groupKey}-${group.groupId}-${index}`}
                  group={group}
                  onRowClick={onRowClick}
                  columns={visibleOrder}
                  styles={styles}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {hasMore && !loading && !error && groups.length > 0 && (
        <div className="mt-3 flex flex-col items-center gap-2">
          {lastResultDate && (
            <div className="text-xs text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
              Showing {groups.length} results through {lastResultDate}
            </div>
          )}
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              fontFamily: 'Raleway, sans-serif',
              backgroundColor: loadingMore ? styles.secondaryBg : styles.primaryBg,
              color: loadingMore ? styles.textSecondary : styles.textPrimary,
              border: `1px solid ${styles.borderColor}`
            }}
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </>
  );
};

export default GroupsTable;
